import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  REWARD_ICONS,
  useSettings,
  type RewardCompletionMode,
  type RewardIcon,
  type RewardSettings,
  type RewardWhen,
} from './SettingsContext';
import { useT } from '../i18n/i18n';

const WHEN_LABEL: Record<RewardWhen, string> = {
  COMPLETION: 'When they finish',
  EXIT: 'Whenever they leave the board',
  APPROVAL: 'Only when you say so',
};

const MODE_LABEL: Record<string, string> = {
  SESSION: 'A finished sitting at the board',
  EVERY_N: 'Every so many correct answers',
};

export default function RewardSection() {
  const t = useT();
  const { settings, update } = useSettings();
  const thinningAvailable = settings.features.thinning;
  const [draft, setDraft] = useState<RewardSettings>(settings.reward);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDraft(settings.reward); }, [settings.reward]);

  function patch<K extends keyof RewardSettings>(key: K, value: RewardSettings[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
    setError(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await update({ reward: draft });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save the reward.'));
    } finally {
      setBusy(false);
    }
  }

  const glyph = REWARD_ICONS[draft.icon];
  const plural = draft.name.trim() || t('rewards');

  return (
    <div className="card">
      <h2>{t('Rewards')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>
        {t('Something a learner collects for their work. You choose what it is called, what it looks like and when it arrives. Rewards are only ever added — nothing takes them away.')}
      </p>

      {settings.features.preferenceAssessment && (
        <div className="toolbar">
          <Link className="btn secondary" to="/preferences">{t('Put the rewards in order')}</Link>
        </div>
      )}

      {error && <div className="error" role="alert">{error}</div>}

      <div className="field">
        <span className="field-label">{t('Rewards')}</span>
        <label className="check">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => patch('enabled', e.target.checked)}
          />
          {t('Give rewards on this account')}
        </label>
        <span className="field-hint">
          {t('Turning this off hides them everywhere at once, including on programs that set their own.')}
        </span>
      </div>

      {draft.enabled && (
        <>
          <div className="row">
            <div className="field">
              <label htmlFor="reward-name">{t('What are they called?')}</label>
              <input
                id="reward-name"
                value={draft.name}
                maxLength={30}
                placeholder={t('stars')}
                onChange={(e) => patch('name', e.target.value)}
              />
              <span className="field-hint">{t('Whatever the learner calls them — stars, points, tokens.')}</span>
            </div>

            <div className="field">
              <label htmlFor="reward-amount">{t('How many at a time?')}</label>
              <input
                id="reward-amount"
                type="number"
                min={1}
                max={10}
                value={draft.amount}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isNaN(n)) patch('amount', Math.min(10, Math.max(1, n)));
                }}
              />
            </div>
          </div>

          <div className="field">
            <span className="field-label">{t('What do they look like?')}</span>
            <div className="icon-choices" role="radiogroup" aria-label={t('Reward icon')}>
              {(Object.keys(REWARD_ICONS) as RewardIcon[]).map((key) => (
                <label
                  key={key}
                  className={`icon-choice${draft.icon === key ? ' is-selected' : ''}`}
                  title={t(key)}
                >
                  <input
                    type="radio"
                    name="reward-icon"
                    value={key}
                    checked={draft.icon === key}
                    onChange={() => patch('icon', key)}
                  />
                  <span aria-hidden="true">{REWARD_ICONS[key]}</span>
                  <span className="visually-hidden">{t(key)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label htmlFor="reward-when">{t('When are they given?')}</label>
              <select
                id="reward-when"
                value={draft.when}
                onChange={(e) => patch('when', e.target.value as RewardWhen)}
              >
                {(Object.keys(WHEN_LABEL) as RewardWhen[]).map((w) => (
                  <option key={w} value={w}>{t(WHEN_LABEL[w])}</option>
                ))}
              </select>
              {draft.when === 'APPROVAL' && (
                <span className="field-hint">
                  {t('A button appears at the end of a sitting and beside the program, so you can give them in the moment or afterwards.')}
                </span>
              )}
            </div>

            {draft.when === 'COMPLETION' && (
              <div className="field">
                <label htmlFor="reward-mode">{t('What counts as finishing?')}</label>
                <select
                  id="reward-mode"
                  value={draft.completionMode === 'MASTERY' ? 'SESSION' : draft.completionMode}
                  onChange={(e) => patch('completionMode', e.target.value as RewardCompletionMode)}
                >
                  {Object.keys(MODE_LABEL).map((m) => (
                    <option key={m} value={m}>{t(MODE_LABEL[m])}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {draft.when === 'COMPLETION' && draft.completionMode === 'EVERY_N' && (
            <>
              <div className="row">
                <div className="field">
                  <label htmlFor="reward-everyn">{t('How many correct answers?')}</label>
                  <input
                    id="reward-everyn"
                    type="number"
                    min={1}
                    max={100}
                    value={draft.everyN}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isNaN(n)) patch('everyN', Math.min(100, Math.max(1, n)));
                    }}
                  />
                </div>
              </div>

              {thinningAvailable && (
                <>
                  <div className="field">
                    <span className="field-label">{t('Over time')}</span>
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={draft.thinning}
                        onChange={(e) => patch('thinning', e.target.checked)}
                      />
                      {t('Ask for more work each time')}
                    </label>
                    <span className="field-hint">
                      {t('The gap widens as they succeed, so {name} stay worth having instead of becoming something the learner needs.', { name: plural })}
                    </span>
                  </div>

                  {draft.thinning && (
                    <div className="row">
                      <div className="field">
                        <label htmlFor="reward-thinstep">{t('Widen the gap by')}</label>
                        <input
                          id="reward-thinstep"
                          type="number"
                          min={1}
                          max={50}
                          value={draft.thinStep}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            if (!Number.isNaN(n)) patch('thinStep', Math.min(50, Math.max(1, n)));
                          }}
                        />
                        <span className="field-hint">{t('Extra answers needed for the next one.')}</span>
                      </div>
                      <div className="field">
                        <label htmlFor="reward-thinmax">{t('But never more than')}</label>
                        <input
                          id="reward-thinmax"
                          type="number"
                          min={1}
                          max={100}
                          value={draft.thinMax}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            if (!Number.isNaN(n)) patch('thinMax', Math.min(100, Math.max(1, n)));
                          }}
                        />
                        <span className="field-hint">{t('The widest the gap ever gets.')}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div className="banner" role="status">
            <span style={{ fontSize: '1.4em' }} aria-hidden="true">{glyph.repeat(draft.amount)}</span>{' '}
            {draft.when === 'APPROVAL'
              ? t('{amount} {name} when you award them', { amount: draft.amount, name: plural })
              : draft.when === 'EXIT'
                ? t('{amount} {name} each time they leave the board', { amount: draft.amount, name: plural })
                : draft.completionMode === 'EVERY_N'
                  ? t('{amount} {name} every {n} correct answers', { amount: draft.amount, name: plural, n: draft.everyN })
                  : t('{amount} {name} for a finished sitting', { amount: draft.amount, name: plural })}
          </div>
        </>
      )}

      <div className="form-actions">
        <button className="btn" onClick={save} disabled={busy}>
          {busy ? t('Saving…') : t('Save rewards')}
        </button>
        {saved && <span className="muted" role="status">{t('Saved.')}</span>}
      </div>
    </div>
  );
}
